import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import {
  createManualRemittanceEvent,
  getEligibleManualRemittanceEventTypes,
  getProfitAndLossSummary,
} from "../../../services/neo/neo";

const FX_RATE_EVENT_TYPES = [
  "CHECKBOOK_RECOVERY",
  "DOUBLE_PAYOUT_RECOVERY",
];

const EMPTY_MANUAL_FORM = {
  event_type: "",
  amount: "",
  currency: "USD",
  txn_id: "",
  final_fx_rate: "",
};

const SUMMARY_CARDS = [
  {
    key: "total_profit",
    label: "Total Profit",
    accent: "#1b5e20",
    background: "#e8f5e9",
  },
  {
    key: "total_loss",
    label: "Total Loss",
    accent: "#b71c1c",
    background: "#ffebee",
  },
  {
    key: "net_profit",
    label: "Net Profit",
    accent: "#0d47a1",
    background: "#e3f2fd",
  },
];

const METRIC_ROWS = [
  { key: "total_profit", label: "Total Profit", type: "currency" },
  { key: "total_loss", label: "Total Loss", type: "currency" },
  { key: "net_profit", label: "Net Profit", type: "currency" },
  { key: "total_abound_cashback", label: "Total Abound Cashback" },
  { key: "total_zynk_fee", label: "Total Zynk Fee" },
  { key: "cash_in_abound", label: "Cash In Abound" },
  { key: "transfer_fee", label: "Transfer Fee" },
  { key: "fx_conversion_fee", label: "FX Conversion Fee" },
  { key: "refunded_plus_payout", label: "Refunded + Payout" },
  { key: "r10_plus_refunded", label: "R10 + Refunded" },
  { key: "effective_promo_gain", label: "Effective Promo Gain" },
  {
    key: "checkbook_fraud_number",
    label: "Checkbook Fraud Number",
    type: "count",
  },
  { key: "checkbook_recovery", label: "Checkbook Recovery" },
  { key: "double_payout_recovery", label: "Double Payout Recovery" },
  { key: "stripe_recovery_link", label: "Stripe Recovery Link" },
  {
    key: "double_payout_numbers",
    label: "Double Payout Numbers",
    type: "count",
  },
  { key: "thunes_credit_limit", label: "Thunes Credit Limit" },
  { key: "revenue_out", label: "Revenue Out" },
  { key: "finstack_hole", label: "Finstack Hole" },
];

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCount = (value) => Number(value || 0).toLocaleString("en-US");

const formatMetricValue = (value, type) => {
  if (type === "count") {
    return formatCount(value);
  }

  return formatCurrency(value);
};

const getYearlyBreakdown = (byMonthData) => {
  if (!byMonthData || typeof byMonthData !== "object") {
    return [];
  }

  return Object.entries(byMonthData).sort(
    ([currentYear], [nextYear]) => Number(nextYear) - Number(currentYear)
  );
};

function ProfitAndLoss() {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manualForm, setManualForm] = useState(EMPTY_MANUAL_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const yearlyBreakdown = getYearlyBreakdown(summary?.by_month);
  const showFinalFxRate =
    manualForm.currency?.toUpperCase() === "INR" &&
    FX_RATE_EVENT_TYPES.includes(manualForm.event_type);

  const fetchProfitAndLossSummary = async () => {
    setLoading(true);
    setError("");

    const response = await getProfitAndLossSummary();

    setLoading(false);

    if (response?.success) {
      setSummary(response || {});
      return;
    }

    const errorMessage =
      response?.error?.message ||
      response?.message ||
      "Failed to load profit and loss data";

    setError(errorMessage);
    Swal.fire({
      title: "Warning",
      text: errorMessage,
      icon: "warning",
      confirmButtonText: "Ok",
    });
  };

  const fetchManualEventTypes = async () => {
    setEventTypesLoading(true);

    const response = await getEligibleManualRemittanceEventTypes();
    setEventTypesLoading(false);

    if (response?.success && Array.isArray(response?.response)) {
      setEventTypes(response.response);
      return;
    }

    const errorMessage =
      response?.error?.message ||
      response?.message ||
      "Failed to load manual event types";

    Swal.fire({
      title: "Warning",
      text: errorMessage,
      icon: "warning",
      confirmButtonText: "Ok",
    });
  };

  useEffect(() => {
    fetchProfitAndLossSummary();
    fetchManualEventTypes();
  }, []);

  const openDialog = () => {
    setManualForm({
      ...EMPTY_MANUAL_FORM,
      event_type: eventTypes[0] || "",
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (submitLoading) {
      return;
    }

    setDialogOpen(false);
    setManualForm(EMPTY_MANUAL_FORM);
    setFormErrors({});
  };

  const handleFormChange = (field, value) => {
    setManualForm((currentForm) => ({
      ...currentForm,
      [field]: field === "currency" ? value.toUpperCase() : value,
      ...(field === "currency" && value.toUpperCase() !== "INR"
        ? { final_fx_rate: "" }
        : {}),
      ...(field === "event_type" && !FX_RATE_EVENT_TYPES.includes(value)
        ? { final_fx_rate: "" }
        : {}),
    }));
    setFormErrors((currentErrors) => ({ ...currentErrors, [field]: "" }));
  };

  const validateManualForm = () => {
    const errors = {};
    const amount = Number(manualForm.amount);
    const finalFxRate = Number(manualForm.final_fx_rate);

    if (!manualForm.event_type) {
      errors.event_type = "Event type is required";
    }

    if (!manualForm.amount || Number.isNaN(amount)) {
      errors.amount = "Amount is required";
    }

    if (!manualForm.currency) {
      errors.currency = "Currency is required";
    } else if (!["INR", "USD"].includes(manualForm.currency.toUpperCase())) {
      errors.currency = "Currency must be INR or USD";
    }

    if (showFinalFxRate && (!manualForm.final_fx_rate || finalFxRate <= 0)) {
      errors.final_fx_rate = "Final FX rate must be greater than 0";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildManualPayload = () => {
    const payload = {
      event_type: manualForm.event_type,
      amount: Number(manualForm.amount),
      currency: manualForm.currency.toUpperCase(),
    };

    if (manualForm.txn_id?.trim()) {
      payload.txn_id = manualForm.txn_id.trim();
    }

    if (showFinalFxRate) {
      payload.final_fx_rate = Number(manualForm.final_fx_rate);
    } else {
      payload.final_fx_rate = 0;
    }

    return payload;
  };

  const submitManualEvent = async (event) => {
    event.preventDefault();

    if (!validateManualForm()) {
      return;
    }

    setSubmitLoading(true);
    const response = await createManualRemittanceEvent(buildManualPayload());
    setSubmitLoading(false);

    if (response?.success) {
      setDialogOpen(false);
      setManualForm(EMPTY_MANUAL_FORM);
      setFormErrors({});
      fetchProfitAndLossSummary();
      Swal.fire({
        title: "Success",
        text: `Manual event saved successfully. Event ID: ${response.event_id}`,
        icon: "success",
        confirmButtonText: "Ok",
      });
      return;
    }

    Swal.fire({
      title: "Oops",
      text:
        response?.error?.message ||
        response?.message ||
        "Failed to submit manual remittance event",
      icon: "warning",
      confirmButtonText: "Ok",
    });
  };

  return (
    <Box sx={{ px: 6 }} className="main-box">
      <Box
        sx={{
          py: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h4" component="h4" sx={{ color: "#33343a" }}>
            Profit and Loss
          </Typography>
          <Typography variant="body1" sx={{ color: "#6b7280", mt: 1 }}>
            Full remittance P&L dashboard with all-time and month-wise totals.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {loading && <CircularProgress size={26} />}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openDialog}
            disabled={eventTypesLoading || !eventTypes.length}
          >
            Add Manual Entry
          </Button>
        </Box>
      </Box>

      {error && !loading ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {SUMMARY_CARDS.map((item) => (
          <Grid item xs={12} md={4} key={item.key}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 3,
                backgroundColor: item.background,
                boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  sx={{ color: item.accent, fontWeight: 600, mb: 1 }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ color: "#111827", fontWeight: 700 }}
                >
                  {formatCurrency(summary?.[item.key])}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
        }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: "#1f2937" }}>
            More Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {METRIC_ROWS.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell align="right">
                      {formatMetricValue(summary?.[row.key], row.type)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
        }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: "#1f2937" }}>
            Year Wise Profit and Loss
          </Typography>

          {yearlyBreakdown.length > 0 ? (
            yearlyBreakdown.map(([year, monthEntries]) => (
              <Accordion
                key={year}
                defaultExpanded={year === yearlyBreakdown[0]?.[0]}
                disableGutters
                sx={{
                  mb: 2,
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px !important",
                  overflow: "hidden",
                  boxShadow: "none",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: "#f8fafc",
                    px: 3,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                    {year}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, py: 0 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Month</TableCell>
                          {METRIC_ROWS.map((row) => (
                            <TableCell
                              key={row.key}
                              align="right"
                              sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                            >
                              {row.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(monthEntries || []).map((monthData, index) => {
                          const [monthName, metrics = {}] =
                            Object.entries(monthData || {})[0] || [];

                          return (
                            <TableRow key={`${year}-${monthName || index}`}>
                              <TableCell>{monthName || "-"}</TableCell>
                              {METRIC_ROWS.map((row) => (
                                <TableCell
                                  key={row.key}
                                  align="right"
                                  sx={{ whiteSpace: "nowrap" }}
                                >
                                  {formatMetricValue(
                                    metrics?.[row.key],
                                    row.type
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No year-wise profit and loss data available.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <form onSubmit={submitManualEvent}>
          <DialogTitle>Add Manual Remittance Entry</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={Boolean(formErrors.event_type)}>
                  <InputLabel id="manual-event-type-label">
                    Event Type
                  </InputLabel>
                  <Select
                    labelId="manual-event-type-label"
                    value={manualForm.event_type}
                    label="Event Type"
                    onChange={(event) =>
                      handleFormChange("event_type", event.target.value)
                    }
                  >
                    {eventTypes.map((eventType) => (
                      <MenuItem value={eventType} key={eventType}>
                        {eventType}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.event_type ? (
                    <Typography variant="caption" color="error">
                      {formErrors.event_type}
                    </Typography>
                  ) : null}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={manualForm.amount}
                  onChange={(event) =>
                    handleFormChange("amount", event.target.value)
                  }
                  inputProps={{ step: "0.01" }}
                  error={Boolean(formErrors.amount)}
                  helperText={formErrors.amount}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(formErrors.currency)}>
                  <InputLabel id="manual-currency-label">Currency</InputLabel>
                  <Select
                    labelId="manual-currency-label"
                    value={manualForm.currency}
                    label="Currency"
                    onChange={(event) =>
                      handleFormChange("currency", event.target.value)
                    }
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="INR">INR</MenuItem>
                  </Select>
                  {formErrors.currency ? (
                    <Typography variant="caption" color="error">
                      {formErrors.currency}
                    </Typography>
                  ) : null}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Transaction ID"
                  value={manualForm.txn_id}
                  onChange={(event) =>
                    handleFormChange("txn_id", event.target.value)
                  }
                  helperText="Recommended. Entries without this will not appear in summary totals."
                />
              </Grid>

              {showFinalFxRate ? (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Final FX Rate"
                    type="number"
                    value={manualForm.final_fx_rate}
                    onChange={(event) =>
                      handleFormChange("final_fx_rate", event.target.value)
                    }
                    inputProps={{ step: "0.0001", min: "0" }}
                    error={Boolean(formErrors.final_fx_rate)}
                    helperText={formErrors.final_fx_rate}
                  />
                </Grid>
              ) : null}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={submitLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitLoading}>
              {submitLoading ? <CircularProgress size={22} /> : "Submit"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default ProfitAndLoss;
